import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import './styles/modern-dashboard.css';
import './styles/dashboard-design-system.css';
import './styles/mobile-dashboard-compact.css'; // MUST BE LAST to override for mobile

// Authentication
import { AuthProvider, useAuth } from './hooks/useAuth';

// Game Context
import { GameProvider } from './context/GameContext';
import { MultiRoomProvider } from './context/MultiRoomContext';
import { NotificationProvider } from './context/NotificationContext';

// Utilities
import { cleanupStaleRoomData } from './utils/storageCleanup';

// Device Detection

// Layouts
import ResponsiveLayout from './layouts/ResponsiveLayout';

// Pages
import Login from './pages/Login/Login';
import Register from './pages/Register/Register';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
import DashboardPage from './pages/Dashboard/DashboardPage';
import RoomsPage from './pages/Rooms/RoomsPage';
import CreateRoomPage from './pages/Rooms/CreateRoomPage';
import ProfilePage from './pages/Profile/ProfilePage';
import SettingsPage from './pages/Settings/SettingsPage';
import GameLobbyPage from './pages/Game/GameLobbyPage';
import GamePlayPage from './pages/Game/GamePlayPage';
import StatisticsDashboard from './pages/Statistics/StatisticsDashboard';
import AdminPanel from './pages/Admin/AdminPanel';
// Future: import RummyGamePage from './pages/Game/RummyGamePage';
// Future: import PokerGamePage from './pages/Game/PokerGamePage';

// Dynamic game loader for extensibility
const GameTypeRouter: React.FC<{ gameType: string }> = ({ gameType }) => {
  switch (gameType) {
    case 'kadinitidi':
      return <GamePlayPage />;
    // case 'rummy':
    //   return <RummyGamePage roomId={roomId} />;
    // case 'poker':
    //   return <PokerGamePage roomId={roomId} />;
    default:
      return <div>Unknown game type: {gameType}</div>;
  }
};

import { useParams } from 'react-router-dom';
import SimpleResponsiveTest from './pages/SimpleResponsiveTest';
import ResponsiveTestPage from './pages/ResponsiveTestPage';
import MinimalTest from './pages/MinimalTest';
// Wrapper to extract params for GameTypeRouter
const GameTypeRouteWrapper: React.FC = () => {
  const { gameType, roomId } = useParams();
  if (!gameType || !roomId) return <div>Missing game type or room ID</div>;
  return <GameTypeRouter gameType={gameType} />;
};

// Protected Route Component with Auto-Join Logic
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const [checkingActiveGame, setCheckingActiveGame] = React.useState(false);
  const [activeGameRoomId, setActiveGameRoomId] = React.useState<string | null>(null);
  const location = useLocation();

  // Only auto-redirect to active game on dashboard or root, not on stats/profile/rooms etc.
  const shouldAutoRedirect = React.useMemo(() => {
    const path = location.pathname || '';
    return path === '/' || path === '/dashboard';
  }, [location.pathname]);

  React.useEffect(() => {
    const checkActiveGame = async () => {
      if (!isAuthenticated || isLoading) return;
      if (!shouldAutoRedirect) return; // Don't hijack routes like /statistics

      try {
        setCheckingActiveGame(true);
        const { gameRoomService } = await import('./services/gameRoomService');
        const response = await gameRoomService.getUserActiveRooms();
        
        if (response.success && response.data) {
          const activeRooms = response.data.activeRooms;
          
          // Find first active room that's in progress
          const gameInProgress = activeRooms.find(room => 
            room.status === 'playing' || room.status === 'in_progress'
          );
          
          if (gameInProgress) {
            console.log('🎮 Found active game, redirecting to room:', gameInProgress.roomId);
            setActiveGameRoomId(gameInProgress.roomId);
            return;
          }
        }
        
        setCheckingActiveGame(false);
      } catch (error) {
        console.error('Error checking for active games:', error);
        setCheckingActiveGame(false);
      }
    };

    checkActiveGame();
  }, [isAuthenticated, isLoading, shouldAutoRedirect]);

  if (isLoading || checkingActiveGame) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">
          {checkingActiveGame ? 'Checking for active games...' : 'Loading...'}
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Auto-redirect to active game from dashboard/root only
  if (shouldAutoRedirect && activeGameRoomId) {
    return <Navigate to={`/game/${activeGameRoomId}`} replace />;
  }

  return <>{children}</>;
};

// Responsive multi-router layout
const AppLayoutWrapper: React.FC = () => (
  <MultiRoomProvider>
    <GameProvider>
      <ResponsiveLayout />
    </GameProvider>
  </MultiRoomProvider>
);

// Main App Component with Router
function App() {
  // Clean up stale room data on app initialization
  React.useEffect(() => {
    cleanupStaleRoomData();
  }, []);

  return (
    <AuthProvider>
      <NotificationProvider position="top-left">
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true
          }}
        >
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            
            {/* Testing Routes (Public) */}
            <Route path="/test-responsive" element={<ResponsiveTestPage />} />
            <Route path="/test-simple" element={<SimpleResponsiveTest />} />
            <Route path="/test-minimal" element={<MinimalTest />} />

            {/* Protected Routes - Responsive multi-router layout */}
            <Route path="/" element={
              <ProtectedRoute>
                <AppLayoutWrapper />
              </ProtectedRoute>
            }>
              <Route index element={<Navigate to="/dashboard" replace />} />
              
              {/* Core Pages (MVP) */}
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="rooms" element={<RoomsPage />} />
              <Route path="rooms/create" element={<CreateRoomPage />} />
              
              {/* Statistics */}
              <Route path="statistics" element={<StatisticsDashboard />} />
              
              {/* Profile & Settings */}
              <Route path="profile" element={<ProfilePage />} />
              <Route path="profile/*" element={<ProfilePage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="settings/*" element={<SettingsPage />} />
              
              {/* Admin Panel */}
              <Route path="admin" element={<AdminPanel />} />
              
              {/* Game Flow */}
              <Route path="lobby/:roomId" element={<GameLobbyPage />} />
              <Route path="game/:roomId" element={<GamePlayPage />} />
              <Route path="game/:gameType/:roomId" element={<GameTypeRouteWrapper />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;
